/** Looping gesture demo for tour steps that describe a drag-and-drop or a
 * multi-step interaction. Rendered inside the guided-tour tooltip.
 *
 * `kind` → which animation to play. Currently supported:
 *  - commitment-drag   → card being dragged between two columns (Today ↔ Tomorrow)
 *  - milestone-slide   → milestone progress slider being swiped to complete
 */
export function GestureDemo({ kind }) {
  if (kind === 'commitment-drag') return <CommitmentDragDemo />;
  if (kind === 'milestone-slide') return <MilestoneSlideDemo />;
  return null;
}

/** Two mini-columns and a card that hops from the first to the second and
 *  back on a 3.2s loop. Everything is CSS-driven — no JS timer, no external
 *  images. Stays crisp on retina and remains accessible (aria-hidden). */
function CommitmentDragDemo() {
  return (
    <div
      aria-hidden="true"
      className="w-full rounded-md border overflow-hidden select-none"
      style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--surface-0))' }}
      data-testid="gesture-commitment-drag"
    >
      <div className="p-2.5">
        <div className="grid grid-cols-2 gap-2 relative h-[110px]">
          <MiniColumn label="Today" testId="demo-col-today" />
          <MiniColumn label="Tomorrow" testId="demo-col-tomorrow" />

          {/* The travelling card. Absolutely positioned inside the grid so
              its keyframes can move across both columns cleanly. */}
          <div className="commit-demo-card absolute top-[26px] left-[8px] w-[calc(50%-14px)]
                          rounded-md bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]
                          px-2 py-1 text-[10.5px] font-semibold shadow-md flex items-center gap-1">
            <span className="commit-demo-dot" />
            Send WO draft
          </div>

          {/* Cursor / pointer that "grabs" the card */}
          <svg className="commit-demo-cursor absolute" width="18" height="20" viewBox="0 0 18 20" aria-hidden="true">
            <path d="M2 2 L2 15 L6 12 L9 18 L12 17 L9 11 L15 11 Z" fill="hsl(var(--foreground))" stroke="white" strokeWidth="1" />
          </svg>
        </div>
        <div className="mt-1.5 text-center text-[10.5px] text-[hsl(var(--foreground-muted))]">
          Drag between columns to reschedule
        </div>
      </div>
    </div>
  );
}

function MiniColumn({ label, testId }) {
  return (
    <div className="rounded border text-[10px] leading-tight bg-[hsl(var(--surface-1))] p-1.5" data-testid={testId}
         style={{ borderColor: 'hsl(var(--border))' }}>
      <div className="text-[9px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold mb-1">
        {label}
      </div>
      <div className="h-[16px] rounded bg-[hsl(var(--surface-2))]" />
      <div className="h-[16px] rounded bg-[hsl(var(--surface-2))] mt-1 opacity-60" />
    </div>
  );
}

/** Milestone slider — a horizontal track with a thumb that slides across on a
 *  3.6s loop, ending in a "completed" flash. Emulates the swipe-to-complete
 *  gesture used on Master Projects milestone cards. */
function MilestoneSlideDemo() {
  return (
    <div
      aria-hidden="true"
      className="w-full rounded-md border overflow-hidden select-none"
      style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--surface-0))' }}
      data-testid="gesture-milestone-slide"
    >
      <div className="p-2.5">
        {/* Fake milestone header row */}
        <div className="flex items-center gap-1.5 mb-2">
          <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--warning))]" />
          <span className="text-[10.5px] font-semibold flex-1 truncate">Client review — Round 2</span>
          <span className="text-[9px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))]">In review</span>
        </div>
        {/* The slider track + thumb + travelling fill */}
        <div className="relative h-[26px] rounded-full bg-[hsl(var(--surface-2))] overflow-hidden">
          <div className="milestone-demo-fill absolute inset-y-0 left-0 rounded-full bg-[hsl(var(--primary))]" />
          <div className="absolute inset-0 flex items-center justify-center text-[10.5px] font-semibold text-[hsl(var(--foreground))] milestone-demo-label">
            Swipe to mark done →
          </div>
          <div className="milestone-demo-thumb absolute top-1 left-1 w-6 h-6 rounded-full bg-[hsl(var(--surface-1))] border shadow-md grid place-items-center"
               style={{ borderColor: 'hsl(var(--border))' }}>
            <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
              <path d="M2 5 L8 5 M5.5 2 L8 5 L5.5 8" fill="none" stroke="hsl(var(--primary))" strokeWidth="1.4" />
            </svg>
          </div>
        </div>
        <div className="mt-1.5 text-center text-[10.5px] text-[hsl(var(--foreground-muted))]">
          Swipe the thumb across to complete a milestone
        </div>
      </div>
    </div>
  );
}
